import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { unitParse, getScrollbarWidth } from './utils.es.js';
import styled from '@emotion/styled';
import { CloseOutlined } from '@teko-builder/icons';

const Overlay = ({ show, children, parent, onEscPressed, id, }) => {
    const elmRef = useRef();
    const [, setValue] = useState(0);
    const parentElement = typeof document !== 'undefined' ? parent || document.body : null;
    useEffect(() => {
        if (!show) {
            return;
        }
        const elm = document.createElement('div');
        if (id)
            elm.id = id;
        elm.style.position = 'fixed';
        elm.style.top = '0';
        elm.style.left = '0';
        elm.style.right = '0';
        elm.style.bottom = '0';
        elm.style.zIndex = '1070';
        elm.style.display = 'flex';
        elm.style.justifyContent = 'center';
        elm.style.alignItems = 'center';
        elm.style.background = 'rgba(0, 0, 0, 0.1)';
        elmRef.current = elm;
        parentElement.appendChild(elm);
        setValue((value) => value + 1);
        return () => {
            parentElement.removeChild(elm);
        };
    }, [show]);
    const handleDocumentKeyDown = (e) => {
        if (e.keyCode === 27 && onEscPressed) {
            onEscPressed();
        }
    };
    useEffect(() => {
        if (!show) {
            return;
        }
        document.addEventListener('keydown', handleDocumentKeyDown);
        return () => {
            document.removeEventListener('keydown', handleDocumentKeyDown);
        };
    }, [show]);
    if (!show || !elmRef.current) {
        return null;
    }
    return ReactDOM.createPortal(children, elmRef.current);
};

var SizeEnum;
(function (SizeEnum) {
    SizeEnum["xs"] = "xs";
    SizeEnum["s"] = "s";
    SizeEnum["m"] = "m";
    SizeEnum["l"] = "l";
    SizeEnum["xl"] = "xl";
})(SizeEnum || (SizeEnum = {}));

const Modal = (_a) => {
    var { show, onHide, children, size = SizeEnum.m, backDropStyle, parent } = _a, props = __rest(_a, ["show", "onHide", "children", "size", "backDropStyle", "parent"]);
    const parentElement = typeof document !== 'undefined' ? parent || document.body : null;
    useEffect(() => {
        if (typeof document !== 'undefined') {
            if (show) {
                parentElement.style.overflow = 'hidden';
                parentElement.style.paddingRight = `${getScrollbarWidth()}px`;
            }
        }
        return () => {
            parentElement.style.overflow = 'unset';
            parentElement.style.paddingRight = 'unset';
        };
    }, [show]);
    if (!show)
        return null;
    return (jsxs(Overlay, Object.assign({ parent: parentElement, show: show, onEscPressed: onHide }, { children: [jsx(Backdrop, { onClick: onHide, style: backDropStyle }), jsx(ModalContent, Object.assign({ size: size }, props, { children: children }))] })));
};
const Backdrop = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: -1000;
`;
const sizeMaps = {
    [SizeEnum.s]: '400px',
    [SizeEnum.m]: '700px',
    [SizeEnum.l]: '960px',
};
const ModalContent = styled.div(({ theme, size, width, maxHeight, borderRadius, backgroundColor, }) => `
    background-color: ${backgroundColor || theme.colors.white};
    display: flex;
    flex-direction: column;
    width: ${width !== undefined ? unitParse(width) : sizeMaps[size]};
    border-radius: ${borderRadius || theme.borderRadius.m};
    max-height: ${maxHeight || '90%'};
    z-index: 1060;
    position: relative;
    overflow-y: auto;
  `);

const Wrapper$2 = styled.div `
  flex: 1 1 auto;
  padding: 0.5rem 2rem;
  position: relative;
`;
const ModalBody = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    return jsx(Wrapper$2, Object.assign({}, props, { children: children }));
};

const ModalCloseIcon = ({ onHide, closeIcon = jsx(CloseOutlined, { style: { fontSize: 32, color: '#000' } }), className, }) => {
    return (jsx(CloseIconWrapper, Object.assign({ className: className, onClick: onHide }, { children: closeIcon })));
};
const CloseIconWrapper = styled.div(({ size }) => `
  z-index: 1060;
  position: absolute;
  left: 100%;
  transform: translateX(-100%);
  min-width: ${unitParse(size) || '56px'};
  min-height: ${unitParse(size) || '56px'};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`);

const Wrapper$1 = styled.div `
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ModalFooter = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    return jsx(Wrapper$1, Object.assign({}, props, { children: children }));
};

const ModalHeader = ({ children, className, }) => jsx(Wrapper, Object.assign({ className: className }, { children: children }));
const Wrapper = styled.div `
  padding: 1rem;
`;

export { ModalFooter as M, SizeEnum as S, Modal as a, ModalHeader as b, ModalCloseIcon as c, ModalBody as d };
